import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLazyGetReportProjectsQuery,
  useLazyGetReportPinnedProjectsQuery,
  type IReportProject,
} from 'app/services/reportsBoardApi';
import type { ConditionalUI, IGroupedAhu } from '../reportScopes';
import ProjectSearchableSelect from './ProjectSearchableSelect';
import AhuMultiSelect from './AhuMultiSelect';
import CoilAnalyticsDateRange from './CoilAnalyticsDateRange';

/**
 * Selector header (#main_header contents) — port of
 * report/mvc/view/main_header.js + tmpl/main_header.tmpl.
 *
 * Owns the PROJECTS dataset exactly like the Backbone view:
 *  - boot: bare page-1 list + pinned list (Projects collections,
 *    main_header.js:121-135); the page preloader collapses on `onBooted`.
 *  - pagination: 100/page; more pages exist only while the last batch
 *    returned >= 100 rows (scrollProjects :404-432 `list.length < 100`).
 *  - search: '?page=1&name=…' + pinned '?pinnned=true&name=…'
 *    (searchProjects :433-456); the select debounces/lowercases upstream.
 *  - deep-linked project not in page 1 → keep loading pages until found
 *    (checkProject :381-403).
 *  - window focus + localStorage 'create:new_project'/'remove:project' →
 *    full refetch (render() onfocus :139-158).
 *
 * Selector visibility/highlight comes from computeConditionalUI (the page)
 * and is applied as inline display + .highlight-select, matching the jQuery
 * .show()/.hide()/.addClass calls (applyConditionalUI :1050-1121).
 */
interface ReportsHeaderProps {
  projectId: string | null;
  ahusGrouped: IGroupedAhu[];
  reportValue: string | null;
  canGenerate: boolean;
  ui: ConditionalUI;
  onSelectProject: (id: string | null, name: string) => void;
  onAhuIdsChange: (ids: number[]) => void;
  /** Lifts the caption name so the page can build the generate state's
   *  project_name (selected_project_name(), main_header.js:1004-1016 — the
   *  'All' fallback is the button text itself). */
  onProjectNameChange: (name: string) => void;
  /** 5b: the Generate ⟳ — main_header.js generateReport (:501-556) via the
   *  page's kit call. */
  onGenerate: () => void;
  /** Remounts/resets the AHU tree on report/project/single-AHU change (the
   *  legacy full-template re-render). */
  ahuResetKey: string;
  onBooted: () => void;
  /** Dev-sync I — the Coil Analytics reporting period (page-owned, like the
   *  legacy header view's coilAnalyticsStartDate/EndDate). The row renders
   *  only while the coil_analytics report is selected (main_header.js
   *  applyConditionalUI :1281-1283 on merged dev). */
  coilAnalyticsRange: { start: string; end: string };
  onCoilAnalyticsDateChange: (field: 'start' | 'end', iso: string) => void;
}

const ReportsHeader = ({
  projectId,
  ahusGrouped,
  reportValue,
  canGenerate,
  ui,
  onSelectProject,
  onAhuIdsChange,
  onProjectNameChange,
  onGenerate,
  ahuResetKey,
  onBooted,
  coilAnalyticsRange,
  onCoilAnalyticsDateChange,
}: ReportsHeaderProps) => {
  const [fetchProjects] = useLazyGetReportProjectsQuery();
  const [fetchPinned] = useLazyGetReportPinnedProjectsQuery();

  // self.projects (page-1 unfiltered), self.pinned_projects.
  const [mainPage1, setMainPage1] = useState<IReportProject[] | null>(null);
  const [pinned, setPinned] = useState<IReportProject[]>([]);
  // self.projectsPagination — accumulated rows currently shown.
  const [pagination, setPagination] = useState<IReportProject[]>([]);
  // self.pinned_projects_filtered.
  const [pinnedFiltered, setPinnedFiltered] = useState<IReportProject[]>([]);
  const [page, setPage] = useState(1);
  const [filtered, setFiltered] = useState('');
  // Length of the last projects_filtered batch (has-more probe).
  const [lastBatch, setLastBatch] = useState(0);
  const loadingRef = useRef(false);
  const bootedRef = useRef(false);
  // Name of the last explicitly selected project (selected_project_name
  // fallback chain, main_header.js:1004-1016).
  const [selectedName, setSelectedName] = useState<string | null>(null);

  const boot = useCallback(async () => {
    const [rows, pin] = await Promise.all([
      fetchProjects().unwrap().catch((): IReportProject[] => []),
      fetchPinned().unwrap().catch((): IReportProject[] => []),
    ]);
    setMainPage1(rows);
    setPagination(rows);
    setPinned(pin);
    setPage(1);
    setFiltered('');
    if (!bootedRef.current) {
      bootedRef.current = true;
      onBooted();
    }
  }, [fetchProjects, fetchPinned, onBooted]);

  useEffect(() => {
    void boot();
  }, [boot]);

  // window focus refresh when another board created/removed a project
  // (main_header.js:139-158).
  useEffect(() => {
    const onFocus = () => {
      if (
        localStorage.getItem('create:new_project') ||
        localStorage.getItem('remove:project')
      ) {
        localStorage.removeItem('create:new_project');
        localStorage.removeItem('remove:project');
        void boot();
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [boot]);

  // Has-more probe (scrollProjects :407-411): the boot page-1 list when
  // unfiltered and unscrolled, otherwise the last filter() batch.
  const lastLoadedCount =
    page === 1 && !filtered ? (mainPage1?.length ?? 0) : lastBatch;

  const loadNextPage = useCallback(async () => {
    if (loadingRef.current || mainPage1 == null) return;
    if (lastLoadedCount < 100) return;
    loadingRef.current = true;
    const next = page + 1;
    const rows = await fetchProjects({ page: next, name: filtered })
      .unwrap()
      .catch((): IReportProject[] => []);
    setPage(next);
    setLastBatch(rows.length);
    setPagination((prev) => [...prev, ...rows]);
    loadingRef.current = false;
  }, [fetchProjects, page, filtered, lastLoadedCount, mainPage1]);

  // checkProject (:381-403): deep-linked project id not visible yet → keep
  // paging until found or exhausted.
  useEffect(() => {
    if (mainPage1 == null || !projectId) return;
    const found =
      pagination.some((p) => String(p.id) === projectId) ||
      pinned.some((p) => String(p.id) === projectId);
    if (found) return;
    void loadNextPage();
  }, [mainPage1, pinned, pagination, projectId, loadNextPage]);

  // searchProjects (:433-456).
  const searchProjects = useCallback(
    async (val: string) => {
      if (val && filtered === val) return; // :435 duplicate-search guard
      setPage(1);
      if (val) {
        const [rows, pin] = await Promise.all([
          fetchProjects({ page: 1, name: val })
            .unwrap()
            .catch((): IReportProject[] => []),
          fetchPinned({ name: val })
            .unwrap()
            .catch((): IReportProject[] => []),
        ]);
        setPagination(rows);
        setLastBatch(rows.length);
        setPinnedFiltered(pin);
        setFiltered(val);
      } else {
        setPagination(mainPage1 ?? []);
        setPinnedFiltered([]);
        setFiltered('');
      }
    },
    [fetchProjects, fetchPinned, mainPage1, filtered]
  );

  // Rendered list: pinned first, then the paginated main list
  // (main_header.js:265-273 / searchProjects :441-444).
  const list = filtered ? [...pinnedFiltered, ...pagination] : [...pinned, ...pagination];

  // selected_project_name (:1004-1016) → button caption (render :262-264).
  const lookupName =
    pagination.find((p) => String(p.id) === projectId)?.name ||
    pinned.find((p) => String(p.id) === projectId)?.name ||
    pinnedFiltered.find((p) => String(p.id) === projectId)?.name;
  const displayName = projectId ? lookupName || selectedName || 'All' : 'All';

  // Lift the caption to the page for the generate state's project_name
  // (main_header.js:506-508 reads selected_project_name() at Generate time).
  useEffect(() => {
    onProjectNameChange(displayName);
  }, [displayName, onProjectNameChange]);

  const handleSelect = (id: number | null, name: string) => {
    setSelectedName(id != null ? name : null);
    // selectProject resets the search state after picking (:478).
    void searchProjects('');
    onSelectProject(id != null ? String(id) : null, name);
  };

  return (
    <>
      <div className="report-selectors">
        <label
          className={`projects-wrapper${ui.projectsHighlight ? ' highlight-select' : ''}`}
          style={ui.projectsVisible ? undefined : { display: 'none' }}
        >
          <ProjectSearchableSelect
            list={list}
            selectedId={projectId}
            name={displayName}
            onSelect={handleSelect}
            onSearch={(val) => void searchProjects(val)}
            onScrollEnd={() => void loadNextPage()}
          />
        </label>
        <label
          className="ahu-wrapper"
          style={ui.ahuVisible ? undefined : { display: 'none' }}
        >
          <span className="ahu-label">AHU</span>
          <AhuMultiSelect
            key={ahuResetKey}
            ahusGrouped={ahusGrouped}
            reportValue={reportValue}
            onChange={onAhuIdsChange}
          />
          <input type="hidden" id="ahu_select" defaultValue="0" />
        </label>
        {/* Dev-sync I — main_header.tmpl:43-58 on merged dev: the Coil
            Analytics Start/End pickers sit inside .report-selectors after the
            AHU wrapper. */}
        <CoilAnalyticsDateRange
          start={coilAnalyticsRange.start}
          end={coilAnalyticsRange.end}
          visible={reportValue === 'coil_analytics'}
          onChange={onCoilAnalyticsDateChange}
        />
      </div>
      <div className="report-actions">
        <button
          type="button"
          id="generate_report"
          className="updade-btn icon-refresh-1 ka-has-tooltip"
          tooltip="Generate"
          disabled={!canGenerate}
          onClick={onGenerate}
        />
      </div>
    </>
  );
};

export default ReportsHeader;
